import {Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {
    InformationDto,
    InformationLightDto,
    InformationService
} from '../../../_services/configuration-services';
import {ActivatedRoute, Router} from '@angular/router';
import {filter, Subscription, take} from 'rxjs';
import {TranslateUtils} from '../../../_shared/translate-utils';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {DocumentManagementTypeEnum} from '../../../_shared/_components/document-management-component/document-management.component';
import {DocumentViewComponent} from '../../../_shared/_components/document-management-component/document-view/document-view.component';
import {BackofficePermissionsService} from '../../../_shared/backoffice-permissions.service';
import {RolesService} from '../../../_shared/roles-service';
import {RouterService} from '../../../_shared/router-service';
import {PermissionKeys} from '../../../app.constants';
import {faPen} from '@fortawesome/free-solid-svg-icons';
import {CommunicationChannelConfigService} from '../../../_shared/communication-channel-config.service';

@Component({
    selector: 'app-view-info',
    templateUrl: './view-info.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ViewInfoComponent implements OnInit, OnDestroy {

    @ViewChild('documentListComponent') documentListComponent: DocumentViewComponent;

    DocumentManagementTypeEnum = DocumentManagementTypeEnum;

    infoId: string | null = null;
    info!: InformationDto;
    infoIndex!: number;
    infoArray: InformationLightDto[] = [];

    // A backoffice with the GestionCommunication permission previews the feed through the backoffice
    // endpoints; customers go through the my-informations endpoints.
    canManage = false;

    private langChangeSubscription: Subscription;
    crtLang = TranslateUtils.defaultLanguage;

    PermissionKeys = PermissionKeys;

    icons = {
        pen: faPen
    };

    constructor(private readonly route: ActivatedRoute,
                private readonly router: Router,
                private readonly routerService: RouterService,
                private readonly backofficePermissionsService: BackofficePermissionsService,
                private readonly rolesService: RolesService,
                private readonly translateService: TranslateService,
                private readonly informationService: InformationService,
                private readonly communicationChannelConfigService: CommunicationChannelConfigService) {
    }

    ngOnInit(): void {
        this.crtLang = this.translateService.currentLang;
        this.langChangeSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.crtLang = event.lang;
        });

        this.route.params.subscribe(params => {
            this.infoId = params['id'] ?? null;
            this.infoArray =  this.routerService.getObjectFromRouteState('infoArray') ?? [];

            this.resolveCanManage(() => {
                if (this.infoArray.length === 0) {
                    this.loadInformationList();
                } else {
                    this.fetchInformation(this.infoId);
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.langChangeSubscription.unsubscribe();
    }

    hasPreviousInformation(): boolean {
        return this.infoIndex > 0;
    }

    hasNextInformation(): boolean {
        return (this.infoIndex + 1) < this.infoArray.length;
    }

    nextInformation(): void {
        if (this.hasNextInformation()) {
            this.router.navigate(['../' + this.infoArray[this.infoIndex + 1].id], {
                relativeTo: this.route,
                state: { infoArray: this.infoArray }
            });
        }
    }

    previousInformation(): void {
        if (this.hasPreviousInformation()) {
            this.router.navigate(['../' + this.infoArray[this.infoIndex - 1].id], {
                relativeTo: this.route,
                state: { infoArray: this.infoArray }
            });
        }
    }

    back(): void {
        this.router.navigate(['..'], {
            relativeTo: this.route
        })
    }

    private fetchInformation(infoId: string): void {
        const request = this.canManage
            ? this.informationService.getInformation(infoId)
            : this.informationService.getMyInformation(infoId);
        request.pipe().subscribe(info => {
            this.info = info;
            this.infoIndex = this.infoArray.findIndex(i => i.id === infoId);

            // Read status is a customer-only concept; a backoffice preview must not mark it read.
            if (!this.canManage) {
                this.informationService.addReadStatus(infoId).pipe().subscribe(() => {
                    this.communicationChannelConfigService.fetchHasUnreadMessage();
                });
            }

            if(this.documentListComponent) {
                this.documentListComponent.id = infoId;
                this.documentListComponent.fetchData();
            }
        }, _ => {
            this.router.navigate(['..'], {
                relativeTo: this.route
            });
        });
    }

    // A backoffice with the GestionCommunication permission previews the feed through the backoffice
    // endpoints; everyone else (customers) goes through the my-informations endpoints.
    private resolveCanManage(then: () => void): void {
        // Client account: no backoffice permissions are ever loaded → go straight to the customer path.
        if (!this.rolesService.isBackofficeAccount()) {
            this.canManage = false;
            then();
            return;
        }
        // Backoffice: wait until permissions are loaded before branching, otherwise a direct page
        // reload reads them as empty and wrongly falls back to the (empty) self list.
        this.backofficePermissionsService.permissionsLoaded$
            .pipe(filter(loaded => loaded), take(1))
            .subscribe(() => {
                this.canManage = this.backofficePermissionsService.hasPermission(PermissionKeys.gestionCommunication);
                then();
            });
    }

    private loadInformationList(): void {
        const request = this.canManage ? this.informationService.getAll() : this.informationService.getAllActive();
        request.pipe().subscribe(infoArray => {
            this.fetchInformation(this.infoId);
            this.infoArray = infoArray;
        });
    }

}
